/**
 * 🎮 Games Management API Client for Zeta Admin
 * Implements specifications from ADMIN_GAMES_README.md
 */

const getAdminBaseUrl = () => {
  const rawBase =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    "http://localhost:18009/admin";
  const trimmed = rawBase.replace(/\/+$/, "");
  return trimmed.endsWith("/admin") ? trimmed : `${trimmed}/admin`;
};

const getAuthHeaders = () => {
  const token =
    localStorage.getItem("token") || localStorage.getItem("adminToken");
  if (!token) {
    return { "Content-Type": "application/json" };
  }
  const authHeader =
    token.startsWith("Basic ") || token.startsWith("Bearer ")
      ? token
      : `Bearer ${token}`;
  return {
    "Content-Type": "application/json",
    Authorization: authHeader,
  };
};

const handleApiResponse = async (response) => {
  const contentType = response.headers.get("content-type");
  const isJson = contentType && contentType.includes("application/json");
  const result = isJson ? await response.json() : await response.text();

  if (!response.ok || (typeof result === "object" && result.status === false)) {
    const errorMsg =
      (typeof result === "object" &&
        (result?.error?.message ||
          result?.error ||
          result?.sqlMessage ||
          result?.message ||
          result?.details)) ||
      (typeof result === "string" ? result : "API request failed");
    throw new Error(errorMsg);
  }

  return result;
};

// =========================================================================
// 🚀 1. Fetch All Games (Active + Inactive with Stats & Sort Order)
// Endpoint: GET /admin/game/list (or /admin/game)
// =========================================================================
export const fetchAdminGames = async (params = {}) => {
  const baseUrl = getAdminBaseUrl();
  const queryParams = new URLSearchParams();

  if (params.status !== undefined && params.status !== "all" && params.status !== "") {
    queryParams.append("status", String(params.status));
  }
  if (params.search && params.search.trim()) {
    queryParams.append("search", params.search.trim());
  }
  if (params.eventId !== undefined && params.eventId !== "all" && params.eventId !== "") {
    queryParams.append("eventId", String(params.eventId));
  }

  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";
  const endpoints = [
    `${baseUrl}/game/list${queryString}`,
    `${baseUrl}/game${queryString}`,
    `${baseUrl}/games${queryString}`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error connecting to Games API");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

// =========================================================================
// 🔢 2. Reorder Games (Set Priority Ordering: 1st, 2nd, 3rd)
// Endpoint: PUT /admin/game/reorder (or POST)
// =========================================================================
export const saveGameOrder = async (reorderedGamesList) => {
  const baseUrl = getAdminBaseUrl();
  
  // Format payload according to API documentation:
  // Supports { games: [{ id, sortOrder }] } or { order: [id1, id2] }
  const formattedGames = reorderedGamesList.map((game, index) => ({
    id: game.id,
    gameKey: game.gameKey || game.key || game.route,
    sortOrder: index + 1,
  }));

  const orderArray = reorderedGamesList.map((g) => g.id).filter(Boolean);

  const payload = {
    games: formattedGames,
    order: orderArray,
  };

  const endpoints = [
    { url: `${baseUrl}/game/reorder`, method: "PUT" },
    { url: `${baseUrl}/game/reorder`, method: "POST" },
    { url: `${baseUrl}/games/reorder`, method: "PUT" },
  ];

  let lastResponse;
  for (const item of endpoints) {
    try {
      const response = await fetch(item.url, {
        method: item.method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404 && response.status !== 405) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (item === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error while saving game order");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

// =========================================================================
// ⚡ 3. Single Game Order Update
// Endpoint: PATCH /admin/game/:id/order (or PUT)
// =========================================================================
export const updateSingleGameOrder = async (gameIdOrKey, sortOrder) => {
  const baseUrl = getAdminBaseUrl();
  const payload = { sortOrder: Number(sortOrder) };

  const endpoints = [
    { url: `${baseUrl}/game/${gameIdOrKey}/order`, method: "PATCH" },
    { url: `${baseUrl}/game/${gameIdOrKey}/order`, method: "PUT" },
    { url: `${baseUrl}/game/order/${gameIdOrKey}`, method: "PATCH" },
  ];

  let lastResponse;
  for (const item of endpoints) {
    try {
      const response = await fetch(item.url, {
        method: item.method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404 && response.status !== 405) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (item === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error updating game order");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

// =========================================================================
// 🔘 4. Enable / Disable Game API (Toggle Switch)
// Endpoint: PATCH /admin/game/:id/status (or /admin/game/status/:gameKey)
// =========================================================================
export const toggleGameActiveStatus = async (gameIdOrKey, newStatus) => {
  const baseUrl = getAdminBaseUrl();
  const payload = typeof newStatus === "boolean" ? { isActive: newStatus } : {};

  const endpoints = [
    { url: `${baseUrl}/game/${gameIdOrKey}/status`, method: "PATCH" },
    { url: `${baseUrl}/game/${gameIdOrKey}/status`, method: "PUT" },
    { url: `${baseUrl}/game/status/${gameIdOrKey}`, method: "PATCH" },
    { url: `${baseUrl}/game/${gameIdOrKey}/toggle`, method: "POST" },
  ];

  let lastResponse;
  for (const item of endpoints) {
    try {
      const response = await fetch(item.url, {
        method: item.method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404 && response.status !== 405) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (item === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error toggling game status");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

// =========================================================================
// 🛠️ 5. Update Game Configuration API (Supports Multipart Form Data & Icons)
// Endpoint: PUT /admin/game/:id (or PATCH)
// =========================================================================
export const updateGameSettings = async (gameIdOrKey, formData, iconFile = null) => {
  const baseUrl = getAdminBaseUrl();
  
  // If iconFile is provided or formData is already FormData, send multipart/form-data
  if (iconFile instanceof File || iconFile instanceof Blob || formData instanceof FormData) {
    const data = formData instanceof FormData ? formData : new FormData();
    if (!(formData instanceof FormData)) {
      if (formData.name) data.append("name", formData.name);
      if (formData.description) data.append("description", formData.description);
      if (formData.route !== undefined) data.append("route", formData.route === null ? "" : formData.route);
      if (formData.gameTime !== undefined) data.append("gameTime", String(formData.gameTime));
      if (formData.minPoints !== undefined) data.append("minPoints", String(formData.minPoints));
      if (formData.maxPoints !== undefined) data.append("maxPoints", String(formData.maxPoints));
      if (formData.cooldownSeconds !== undefined) data.append("cooldownSeconds", String(formData.cooldownSeconds));
      if (formData.sortOrder !== undefined) data.append("sortOrder", String(formData.sortOrder));
      if (formData.isActive !== undefined) data.append("isActive", String(Boolean(formData.isActive)));
      if (formData.jackpotPoints !== undefined) data.append("jackpotPoints", String(formData.jackpotPoints));
      if (formData.tripleBarPoints !== undefined) data.append("tripleBarPoints", String(formData.tripleBarPoints));
      if (formData.tripleCherryPoints !== undefined) data.append("tripleCherryPoints", String(formData.tripleCherryPoints));
      if (formData.otherTriplePoints !== undefined) data.append("otherTriplePoints", String(formData.otherTriplePoints));
      if (formData.match2Points !== undefined) data.append("match2Points", String(formData.match2Points));
      if (formData.totalSpins !== undefined) data.append("totalSpins", String(formData.totalSpins));
      if (iconFile) data.append("icon", iconFile);
    }

    const token = localStorage.getItem("token") || localStorage.getItem("adminToken");
    const authHeaders = token
      ? { Authorization: token.startsWith("Basic ") || token.startsWith("Bearer ") ? token : `Bearer ${token}` }
      : {};

    const endpoints = [
      { url: `${baseUrl}/game/${gameIdOrKey}`, method: "PUT" },
      { url: `${baseUrl}/game/${gameIdOrKey}`, method: "PATCH" },
      { url: `${baseUrl}/game/${gameIdOrKey}/settings`, method: "PUT" },
    ];

    let lastResponse;
    for (const item of endpoints) {
      try {
        const response = await fetch(item.url, {
          method: item.method,
          headers: authHeaders, // Let browser set Content-Type with boundary for FormData
          body: data,
        });
        if (response.ok) {
          return handleApiResponse(response);
        }
        lastResponse = response;
        if (response.status !== 404 && response.status !== 405) {
          return handleApiResponse(response);
        }
      } catch (netErr) {
        if (item === endpoints[endpoints.length - 1]) {
          throw new Error(netErr.message || "Network error uploading game icon & settings");
        }
      }
    }
    return handleApiResponse(lastResponse);
  }

  // Standard JSON payload
  const payload = {
    name: formData.name,
    description: formData.description,
    route: formData.route !== undefined ? (formData.route === "" ? null : formData.route) : null,
    gameTime: Number(formData.gameTime ?? 30),
    minPoints: Number(formData.minPoints ?? 0),
    maxPoints: Number(formData.maxPoints ?? 50),
    cooldownSeconds: Number(formData.cooldownSeconds ?? 180),
    isActive: Boolean(formData.isActive === 1 || formData.isActive === true || formData.isActive === "1"),
    ...(formData.sortOrder !== undefined ? { sortOrder: Number(formData.sortOrder) } : {}),
    ...(formData.icon ? { icon: formData.icon } : {}),
    ...(formData.banner ? { banner: formData.banner } : {}),
    ...(formData.jackpotPoints !== undefined ? { jackpotPoints: Number(formData.jackpotPoints) } : {}),
    ...(formData.tripleBarPoints !== undefined ? { tripleBarPoints: Number(formData.tripleBarPoints) } : {}),
    ...(formData.tripleCherryPoints !== undefined ? { tripleCherryPoints: Number(formData.tripleCherryPoints) } : {}),
    ...(formData.otherTriplePoints !== undefined ? { otherTriplePoints: Number(formData.otherTriplePoints) } : {}),
    ...(formData.match2Points !== undefined ? { match2Points: Number(formData.match2Points) } : {}),
    ...(formData.totalSpins !== undefined ? { totalSpins: Number(formData.totalSpins) } : {}),
    ...(formData.pointProbabilities !== undefined ? { pointProbabilities: formData.pointProbabilities } : {}),
  };

  const endpoints = [
    { url: `${baseUrl}/game/${gameIdOrKey}`, method: "PUT" },
    { url: `${baseUrl}/game/${gameIdOrKey}`, method: "PATCH" },
    { url: `${baseUrl}/game/${gameIdOrKey}/settings`, method: "PUT" },
  ];

  let lastResponse;
  for (const item of endpoints) {
    try {
      const response = await fetch(item.url, {
        method: item.method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404 && response.status !== 405) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (item === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error saving game settings");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

// =========================================================================
// 🖼️ 5.5. Dedicated Game Icon Upload API
// Endpoint: POST /admin/game/:id/icon
// =========================================================================
export const uploadGameIconOnly = async (gameIdOrKey, file) => {
  const baseUrl = getAdminBaseUrl();
  const formData = new FormData();
  formData.append("icon", file);
  formData.append("image", file);

  const token = localStorage.getItem("token") || localStorage.getItem("adminToken");
  const authHeaders = token
    ? { Authorization: token.startsWith("Basic ") || token.startsWith("Bearer ") ? token : `Bearer ${token}` }
    : {};

  const endpoints = [
    `${baseUrl}/game/${gameIdOrKey}/icon`,
    `${baseUrl}/game/icon/${gameIdOrKey}`,
    `${baseUrl}/game/${gameIdOrKey}/image`,
    `${baseUrl}/game/icon`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: authHeaders,
        body: formData,
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error uploading icon");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

// =========================================================================
// 📜 6. Game Play Audit History Logs
// Endpoint: GET /admin/game/:gameKey/history?page=1&perPage=20&search=...
// =========================================================================
export const getGameAuditHistory = async (
  gameKey = "tap",
  { page = 1, perPage = 20, search = "", status = "" } = {}
) => {
  const baseUrl = getAdminBaseUrl();
  const queryParams = new URLSearchParams();

  if (page) queryParams.append("page", String(page));
  if (perPage) {
    queryParams.append("perPage", String(perPage));
    queryParams.append("limit", String(perPage));
  }
  if (search && search.trim()) queryParams.append("search", search.trim());
  if (status && status !== "all") queryParams.append("status", status.trim());

  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";
  const genericQuery = queryParams.toString()
    ? `?${queryParams.toString()}&game=${gameKey}&gameType=${gameKey}`
    : `?game=${gameKey}&gameType=${gameKey}`;

  const endpoints = [
    `${baseUrl}/game/${gameKey}/history${queryString}`,
    `${baseUrl}/game/history${genericQuery}`,
    `${baseUrl}/game/${gameKey}/logs${queryString}`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error retrieving game history");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

// Re-export specific helpers for Tap, BoomCards, and Slots backward compatibility
export {
  getBoomCardsSettings,
  updateBoomCardsSettings,
  getBoomCardsHistory,
  getTapGameSettings,
  updateTapGameSettings,
  getTapGameHistory,
  getSlotsSettings,
  updateSlotsSettings,
  getSlotsHistory,
  getSpinwheelSettings,
  updateSpinwheelSettings,
} from "./tapGameApi";

export {
  get2048Settings,
  update2048Settings,
  get2048History,
  get2048Trends,
} from "./game2048Api";

export {
  getFruitBombSettings,
  updateFruitBombSettings,
  getFruitBombHistory,
  getFruitBombTrends,
} from "./fruitBombApi";

export {
  getEventPointsLedger,
} from "./eventPointsApi";

export {
  getTimerSettings,
  updateTimerSettings,
  startTimerConfig,
  getTimerHistory,
} from "./timerGameApi";
